/**
 * Delivery and offline persistence for auto-captured error reports.
 *
 * Auto-capture is fire-and-forget, so delivery must survive a crashing or
 * unloading page and a flaky network:
 *
 *  - {@link deliver} POSTs with `fetch(..., { keepalive: true })` so the
 *    request outlives a navigation/unload (where a normal fetch would be
 *    cancelled). `keepalive` also permits the JSON content-type that
 *    `navigator.sendBeacon` cannot send to a cross-origin endpoint.
 *  - On failure (offline, server down) the wrapped body is persisted to
 *    localStorage and {@link flushQueue} retries on next load and whenever the
 *    browser comes back online.
 *
 * The awaited *manual* report path stays on axios (see {@link ./reportError});
 * this module only backs the automatic path.
 */

import { FEEDBACK_URL } from "./endpoint";
import { safeStorage } from "@/utils/safeStorage";

/** The wrapped `{ label, content, username }` body the feedback BE expects. */
export type FeedbackBody = Record<string, unknown>;

/**
 * Exported for the account-deletion teardown: a deleted account's queued
 * reports (which embed the username) must not be flushed later, possibly
 * under a different person's session.
 */
export const ERROR_REPORT_QUEUE_KEY = "errorReportQueue";
const QUEUE_KEY = ERROR_REPORT_QUEUE_KEY;
const MAX_QUEUED = 20;
const DELIVERY_TIMEOUT_MS = 15_000;

export async function deliver(body: FeedbackBody): Promise<boolean> {
  if (typeof fetch !== "function") {
    return false;
  }
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : undefined;
  const timer = controller
    ? setTimeout(() => controller.abort(), DELIVERY_TIMEOUT_MS)
    : undefined;
  try {
    const res = await fetch(FEEDBACK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
      signal: controller?.signal,
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

function readQueue(): FeedbackBody[] {
  try {
    const raw = safeStorage.get(QUEUE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as FeedbackBody[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(items: FeedbackBody[]): void {
  try {
    if (items.length === 0) {
      safeStorage.remove(QUEUE_KEY);
    } else {
      safeStorage.set(QUEUE_KEY, JSON.stringify(items.slice(-MAX_QUEUED)));
    }
  } catch {
    /* storage full or unavailable — drop silently */
  }
}

export function enqueue(body: FeedbackBody): void {
  const items = readQueue();
  items.push(body);
  writeQueue(items);
}

let flushing = false;

/** Attempts to deliver every queued report, removing each on success. */
export async function flushQueue(): Promise<void> {
  if (flushing) {
    return;
  }
  const items = readQueue();
  if (items.length === 0) {
    return;
  }
  flushing = true;
  // Remove this batch from storage up-front, then re-enqueue only the items
  // that fail to deliver. Writing the survivors back at the *end* instead would
  // clobber any report enqueued concurrently mid-flush (e.g. a new capture
  // firing as the app comes back online) — a lost update.
  writeQueue([]);
  try {
    for (const item of items) {
      // eslint-disable-next-line no-await-in-loop -- sequential to respect rate limits
      const ok = await deliver(item);
      if (!ok) {
        enqueue(item);
      }
    }
  } finally {
    flushing = false;
  }
}
